import React, { useEffect, useRef, useState } from 'react';
import '../style/components/InfoContent.scss';

export default function InfoContent() {
  const ref = useRef(null);
  const [btcLevels, setBtcLevels] = useState(null);

  useEffect(() => {
    const CACHE_KEY = 'btc_6m_levels_v2';
    const TTL = 12 * 3600 * 1000;
    try {
      const c = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
      if (c && Date.now() - c.ts < TTL) { setBtcLevels(c.data); return; }
    } catch {}
    fetch('https://api.coingecko.com/api/v3/coins/bitcoin/market_chart?vs_currency=usd&days=180&interval=daily')
      .then(r => r.json())
      .then(({ prices, total_volumes }) => {
        const vals = prices.map(p => p[1]);
        const high = Math.round(Math.max(...vals));
        const low  = Math.round(Math.min(...vals));
        const buckets = 40;
        const size = (high - low) / buckets;
        const vol = new Array(buckets).fill(0);
        prices.forEach(([, price], i) => {
          const v = (total_volumes[i] || [])[1] || 0;
          const idx = Math.min(Math.floor((price - low) / size), buckets - 1);
          vol[idx] += v;
        });
        const vpoIdx = vol.indexOf(Math.max(...vol));
        const vpo = Math.round(low + (vpoIdx + 0.5) * size);
        const data = { high, low, vpo };
        try { localStorage.setItem(CACHE_KEY, JSON.stringify({ data, ts: Date.now() })); } catch {}
        setBtcLevels(data);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!ref.current) return;
    const container = ref.current;

    const SEQ_STATES = {
      inc:  { cls: 'inc',  label: 'INC'  },
      hold: { cls: 'hold', label: 'HOLD' },
      dec:  { cls: 'dec',  label: 'DEC'  },
    };

    function generateBuffer(len) {
      const buf = [];
      let phase = Math.random() > 0.5 ? 'inc' : 'dec';
      let runLeft = 1 + Math.floor(Math.random() * 3);
      for (let i = 0; i < len; i++) {
        if (runLeft <= 0) {
          if (phase !== 'hold') { phase = 'hold'; runLeft = 1; }
          else { phase = buf[i - 2] === 'inc' || buf[i - 2] === undefined ? 'dec' : 'inc'; runLeft = 1 + Math.floor(Math.random() * 3); }
        }
        buf.push(phase);
        runLeft--;
      }
      return buf;
    }

    function applyRow(cells, buf, ptr) {
      cells.forEach((cell, i) => {
        const s = SEQ_STATES[buf[(ptr + i) % buf.length]];
        cell.className = 'seq-cell ' + s.cls;
        cell.textContent = s.label;
      });
    }

    const allCells = Array.from(container.querySelectorAll('.seq-cell'));
    const heroRow4hr   = allCells.slice(0, 7);
    const heroRowDaily = allCells.slice(7, 14);
    const seqRow4hr    = allCells.slice(14, 21);
    const seqRowDaily  = allCells.slice(21, 28);
    const buf4hr   = generateBuffer(60);
    const bufDaily = generateBuffer(60);
    let ptr4hr = 0, ptrDaily = 0;

    applyRow(heroRow4hr,   buf4hr,   ptr4hr);
    applyRow(heroRowDaily, bufDaily, ptrDaily);
    applyRow(seqRow4hr,    buf4hr,   ptr4hr);
    applyRow(seqRowDaily,  bufDaily, ptrDaily);

    const t1 = setInterval(() => {
      ptr4hr = (ptr4hr + 1) % 60;
      applyRow(heroRow4hr, buf4hr, ptr4hr);
      applyRow(seqRow4hr,  buf4hr, ptr4hr);
    }, 1200);
    const t2 = setInterval(() => {
      ptrDaily = (ptrDaily + 1) % 60;
      applyRow(heroRowDaily, bufDaily, ptrDaily);
      applyRow(seqRowDaily,  bufDaily, ptrDaily);
    }, 2800);

    return () => { clearInterval(t1); clearInterval(t2); };
  }, []);

  return (
    <div ref={ref} className="info-content">

      {/* PRICE SEQUENCER HERO */}
      <section className="ic-hero">
        <div className="ic-hero-content">
          <div className="ic-hero-tag">Systematic Bitcoin Analysis</div>
          <h1 style={{ fontSize: 'clamp(17px, 2.9vw, 35px)' }}>Price Sequencer<span>(XYR)</span></h1>
          <p className="ic-hero-desc">
            Institutional-grade cycle detection and blockchain analytics — engineered to give retail investors the same edge as professional traders. Identify institutional order flow and market structure.
          </p>
        </div>

        <div className="ic-hero-right">
          <div className="ic-hero-visual">
            <div className="ic-dashboard-card">
              <div className="ic-dash-header">
                <span className="ic-dash-title">XYR</span>
                <span className="ic-live-dot"><span className="ic-dot"></span>LIVE FEED</span>
              </div>
              <div className="ic-dash-body">
                <div className="seq-grid">
                  <div className="seq-head"></div>
                  <div className="seq-head">S</div>
                  <div className="seq-head">M</div>
                  <div className="seq-head">T</div>
                  <div className="seq-head">W</div>
                  <div className="seq-head">TH</div>
                  <div className="seq-head">F</div>
                  <div className="seq-head">S</div>

                  <div className="seq-row-label">4HR</div>
                  <div className="seq-cell hold">HOLD</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell hold">HOLD</div>
                  <div className="seq-cell dec">DEC</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell hold">HOLD</div>

                  <div className="seq-row-label">DAILY</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell hold">HOLD</div>
                  <div className="seq-cell inc">INC</div>
                  <div className="seq-cell dec">DEC</div>
                  <div className="seq-cell hold">HOLD</div>
                  <div className="seq-cell inc">INC</div>
                </div>

                <div className="ic-price-row">
                  <div>
                    <div className="ic-price-big">$83,420</div>
                    <div className="ic-price-change">▲ +2.14% today</div>
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '2px', color: 'var(--ic-dim)', marginBottom: '4px' }}>BLOCK HEIGHT</div>
                    <div style={{ fontFamily: "'Orbitron',monospace", fontSize: '13px', color: 'var(--ic-text)' }}>895,241</div>
                  </div>
                </div>

                <div className="ic-session-bar">
                  <span className="ic-session-tag ny">NY 9:30AM</span>
                  <span className="ic-session-tag london">LON 16:40</span>
                  <span className="ic-session-tag sydney">SYD 3:40AM</span>
                </div>
              </div>
            </div>
          </div>

          <div className="ic-hero-stats">
            <div className="ic-stat-item">
              <div className="ic-stat-label">Bullish State</div>
              <div className="ic-stat-value green">INCREASE</div>
            </div>
            <div className="ic-stat-item">
              <div className="ic-stat-label">Transition State</div>
              <div className="ic-stat-value yellow">HOLD</div>
            </div>
            <div className="ic-stat-item">
              <div className="ic-stat-label">Bearish State</div>
              <div className="ic-stat-value red">DECREASE</div>
            </div>
            <div className="ic-stat-item">
              <div className="ic-stat-label">Timeframes</div>
              <div className="ic-stat-value cyan">4HR+D</div>
            </div>
          </div>
        </div>
      </section>

      <hr className="sep" />

      {/* METHODOLOGY */}
      <section style={{ background: 'var(--ic-panel)' }}>
        <div className="section-inner">
          <div className="section-tag">Methodology</div>
          <h2>How XYR Works</h2>
          <p className="section-desc">Bitcoin prices exhibit measurable, recurring cycles. XYR decomposes those cycles into two timeframes — 4-hour and Daily — and delivers actionable cycle readings at the moments that matter most.</p>

          <div className="steps-grid">
            <div className="step-card">
              <div className="step-num">01</div>
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                </svg>
              </div>
              <h3>Cycle Detection</h3>
              <p>Bitcoin's price is decomposed into three nested cycles using lag-reduced filtering: a long-term trend cycle, a medium-amplitude intermediate cycle, and a short-term oscillation. Each cycle resembles a sine wave — XYR tracks where each one is in its rotation and synthesizes them into a single R value.</p>
            </div>
            <div className="step-card">
              <div className="step-num">02</div>
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12 6 12 12 16 14"/>
                </svg>
              </div>
              <h3>Session Timing</h3>
              <p>Readings are taken across 4-hour and daily intervals aligned to US market open, US close, and European overnight open — coinciding with peak transaction volume on the blockchain.</p>
            </div>
            <div className="step-card">
              <div className="step-num">03</div>
              <div className="step-icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
              </div>
              <h3>Real Time Cycle Estimation Analysis</h3>
              <p>R is a normalized value that oscillates between <strong style={{ color: 'var(--ic-red)' }}>−1</strong> (deep oversold) and <strong style={{ color: 'var(--ic-green)' }}>+1</strong> (deep overbought). The system does not trigger on where R sits on that scale — it triggers on <strong style={{ color: 'var(--ic-bright)' }}>when R changes direction</strong>: up = <strong style={{ color: 'var(--ic-green)' }}>INCREASE</strong>, down = <strong style={{ color: 'var(--ic-red)' }}>DECREASE</strong>, turning = <strong style={{ color: 'var(--ic-yellow)' }}>HOLD</strong>.</p>
            </div>
          </div>
        </div>
      </section>

      <hr className="sep" />

      {/* PRICE SEQUENCER */}
      <section>
        <div className="section-inner">
          <div className="seq-explainer">
            <div>
              <div className="section-tag">Price Sequencer</div>
              <h2 style={{ marginBottom: '24px' }}>Read the State,<br/>Not the Number</h2>
              <p style={{ fontSize: '16px', fontWeight: 300, lineHeight: 1.75, color: 'var(--ic-text)' }}>
                The XYR Sequencer outputs a single value called <strong style={{ color: 'var(--ic-bright)' }}>R</strong> — a normalized cycle position ranging from <strong style={{ color: 'var(--ic-red)' }}>−1</strong> to <strong style={{ color: 'var(--ic-green)' }}>+1</strong> — where Bitcoin sits in its current price cycle rotation. Rather than firing when R crosses a fixed threshold, XYR detects the inflection: the moment R changes direction, built from three nested cycle components synthesized into one composite reading. The result: a green cell at R = −0.7 is valid and meaningful — it means the cycle has turned upward from a low point. The <em style={{ color: 'var(--ic-bright)', fontStyle: 'normal', fontWeight: 600 }}>direction</em> is the edge, not the position.
              </p>
            </div>

            <div className="state-visual">
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '16px' }}>SHAPE OF ONE LONG CYCLE</div>

              <div style={{ position: 'relative', background: 'var(--ic-panel2)', border: '1px solid var(--ic-border)', padding: '12px 10px 10px' }}>
                <svg viewBox="0 0 340 168" width="100%" style={{ display: 'block', overflow: 'visible' }}>
                  <rect x="18" y="8" width="302" height="24" fill="rgba(245,194,0,0.07)"/>
                  <rect x="18" y="118" width="302" height="24" fill="rgba(245,194,0,0.07)"/>
                  <line x1="18" y1="8"   x2="320" y2="8"   stroke="rgba(245,194,0,0.35)" strokeWidth="1" strokeDasharray="3,5"/>
                  <line x1="18" y1="32"  x2="320" y2="32"  stroke="rgba(245,194,0,0.35)" strokeWidth="1" strokeDasharray="3,5"/>
                  <line x1="18" y1="118" x2="320" y2="118" stroke="rgba(245,194,0,0.35)" strokeWidth="1" strokeDasharray="3,5"/>
                  <line x1="18" y1="142" x2="320" y2="142" stroke="rgba(245,194,0,0.35)" strokeWidth="1" strokeDasharray="3,5"/>
                  <line x1="18" y1="75" x2="320" y2="75" stroke="#1a1d2e" strokeWidth="1"/>
                  <text x="2" y="10" fontFamily="Share Tech Mono" fontSize="6" fill="#545a72" transform="rotate(-90,5,75)" textAnchor="middle">R</text>
                  <text x="1" y="12"  fontFamily="Share Tech Mono" fontSize="6" fill="rgba(0,229,204,0.45)">+1</text>
                  <text x="1" y="78"  fontFamily="Share Tech Mono" fontSize="6" fill="#545a72"> 0</text>
                  <text x="1" y="145" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(0,229,204,0.45)">-1</text>
                  <text x="246" y="22"  fontFamily="Share Tech Mono" fontSize="6" fill="rgba(245,194,0,0.75)" letterSpacing="0.5">HOLD ZONE (EST.)</text>
                  <text x="246" y="134" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(245,194,0,0.75)" letterSpacing="0.5">HOLD ZONE (EST.)</text>
                  <line x1="20" y1="138" x2="44" y2="118" stroke="#f5c200" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="44" y1="118" x2="146" y2="32" stroke="#00e57a" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="146" y1="32" x2="170" y2="12" stroke="#f5c200" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="170" y1="12" x2="194" y2="32" stroke="#f5c200" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="194" y1="32" x2="296" y2="118" stroke="#e8003a" strokeWidth="2.5" strokeLinecap="round"/>
                  <line x1="296" y1="118" x2="320" y2="138" stroke="#f5c200" strokeWidth="2.5" strokeLinecap="round"/>
                  <text x="62"  y="92" fontFamily="Share Tech Mono" fontSize="7" fill="#00e57a" letterSpacing="1">INC</text>
                  <text x="220" y="92" fontFamily="Share Tech Mono" fontSize="7" fill="#e8003a" letterSpacing="1">DEC</text>
                  <line x1="20"  y1="4" x2="320" y2="4" stroke="rgba(0,229,204,0.4)" strokeWidth="1"/>
                  <line x1="20"  y1="1" x2="20"  y2="7" stroke="rgba(0,229,204,0.4)" strokeWidth="1"/>
                  <line x1="320" y1="1" x2="320" y2="7" stroke="rgba(0,229,204,0.4)" strokeWidth="1"/>
                  <text x="105" y="3.5" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(0,229,204,0.6)" letterSpacing="0.5">EST. CYCLE LENGTH</text>
                  <line x1="18" y1="145" x2="320" y2="145" stroke="#1a1d2e" strokeWidth="1"/>
                  <text x="17"  y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">M</text>
                  <text x="42"  y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">A</text>
                  <text x="67"  y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">M</text>
                  <text x="92"  y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">J</text>
                  <text x="117" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">J</text>
                  <text x="142" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">A</text>
                  <text x="167" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">S</text>
                  <text x="192" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">O</text>
                  <text x="217" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">N</text>
                  <text x="242" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">D</text>
                  <text x="267" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">J</text>
                  <text x="292" y="154" fontFamily="Share Tech Mono" fontSize="7" fill="#545a72">F</text>
                  <text x="128" y="165" fontFamily="Share Tech Mono" fontSize="6.5" fill="#545a72" letterSpacing="2">TIME →</text>
                </svg>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginTop: '8px' }}>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(0,229,122,0.8)', textAlign: 'center', padding: '5px 0', borderTop: '1px solid rgba(0,229,122,0.25)' }}>INC<br/><span style={{ color: 'var(--ic-dim)' }}>Rising phase</span></div>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(245,194,0,0.8)', textAlign: 'center', padding: '5px 0', borderTop: '1px solid rgba(245,194,0,0.25)' }}>HOLD<br/><span style={{ color: 'var(--ic-dim)' }}>Top / bottom est. range</span></div>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(232,0,58,0.8)', textAlign: 'center', padding: '5px 0', borderTop: '1px solid rgba(232,0,58,0.25)' }}>DEC<br/><span style={{ color: 'var(--ic-dim)' }}>Falling phase</span></div>
                </div>

                <div style={{ marginTop: '8px', fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'var(--ic-dim)', textAlign: 'center', paddingTop: '6px', borderTop: '1px solid var(--ic-border)' }}>
                  ⚠ ESTIMATED RANGES ONLY — NOT FINANCIAL ADVICE. FOR ANALYTICAL REFERENCE.
                </div>
              </div>

              <div className="state-cards" style={{ marginTop: '14px' }}>
                <div className="state-card inc">
                  <div className="state-arrow">▲</div>
                  <div className="state-name">INCREASE</div>
                  <div className="state-desc">R slope<br/>positive</div>
                </div>
                <div className="state-card hold">
                  <div className="state-arrow">◉</div>
                  <div className="state-name">HOLD</div>
                  <div className="state-desc">R changing<br/>direction</div>
                </div>
                <div className="state-card dec">
                  <div className="state-arrow">▼</div>
                  <div className="state-name">DECREASE</div>
                  <div className="state-desc">R slope<br/>negative</div>
                </div>
              </div>

              <div className="seq-grid" style={{ marginTop: '16px', maxWidth: '460px' }}>
                <div className="seq-head"></div>
                <div className="seq-head">S</div>
                <div className="seq-head">M</div>
                <div className="seq-head">T</div>
                <div className="seq-head">W</div>
                <div className="seq-head">TH</div>
                <div className="seq-head">F</div>
                <div className="seq-head">S</div>

                <div className="seq-row-label">4HR</div>
                <div className="seq-cell hold">HOLD</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell hold">HOLD</div>
                <div className="seq-cell dec">DEC</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell hold">HOLD</div>

                <div className="seq-row-label">DAILY</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell hold">HOLD</div>
                <div className="seq-cell inc">INC</div>
                <div className="seq-cell dec">DEC</div>
                <div className="seq-cell hold">HOLD</div>
                <div className="seq-cell inc">INC</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <hr className="sep" />

      {/* ECO CALENDAR */}
      <section style={{ background: 'var(--ic-panel)' }}>
        <div className="section-inner">
          <div className="section-tag">Economic Calendar</div>
          <h2>High-Impact Events<br/>Create Spike Conditions</h2>
          <p className="section-desc">Major macro releases — CPI, FOMC, NFP — inject sudden volatility that can override the normal cycle. The Blockhive ECO Calendar tracks these events in real time so you are never caught off guard. These releases trigger institutional order flow — large buy and sell programs execute automatically at the print, breaking prior market structure and establishing new levels before retail participants can react.</p>

          <div className="eco-events-row">
            <span className="eco-event-tag high">🇺🇸 CPI</span>
            <span className="eco-event-tag high">🇺🇸 FOMC</span>
            <span className="eco-event-tag high">🇺🇸 NFP</span>
            <span className="eco-event-tag high">🇺🇸 PPI</span>
            <span className="eco-event-tag med">🇪🇺 ECB RATE</span>
            <span className="eco-event-tag med">🇨🇳 PMI</span>
          </div>

          <div style={{ display: 'flex', gap: '16px', alignItems: 'stretch' }}>

          {/* ECO Calendar illustration — mirrors EcoCalendar.js layout */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              background: '#000', borderRadius: 4,
              border: '2px solid #555', borderRight: '2px solid #1a1a1a', borderBottom: '2px solid #1a1a1a',
              padding: '10px 12px', height: '100%', boxSizing: 'border-box',
              fontFamily: "'Orbitron', sans-serif", color: '#e0e0e0',
            }}>

              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8, paddingBottom: 6, borderBottom: '1px solid rgba(111,192,186,0.25)' }}>
                <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '1.5px', color: '#37E6CF' }}>ECO CALENDAR</span>
                <span style={{ fontSize: 9, color: '#909090' }}>6 events</span>
              </div>

              {/* Clock ticker */}
              <div style={{ overflow: 'hidden', borderBottom: '1px solid rgba(111,192,186,0.25)', padding: '3px 0', marginBottom: 8 }}>
                <span style={{ fontSize: 9, color: '#fff', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>
                  NY 2:14 PM  ·  LON 7:14 PM  ·  CNY 2:14 AM  ·  SYD 4:14 AM  ·  SF 11:14 AM
                </span>
              </div>

              {/* Column headers */}
              <div style={{ display: 'grid', gridTemplateColumns: '50px 18px 1fr 36px 12px 32px 8px', gap: 4, paddingBottom: 4, borderBottom: '1px solid rgba(255,255,255,0.06)', marginBottom: 4 }}>
                {['TIME', '', 'EVENT', 'PREV', 'DIR', 'IN', ''].map((h, i) => (
                  <span key={i} style={{ fontSize: 7, letterSpacing: '0.8px', color: '#d8d8d8', textTransform: 'uppercase' }}>{h}</span>
                ))}
              </div>

              {/* Event rows grouped by day */}
              {[
                {
                  label: 'WED, 6/11',
                  rows: [
                    { time: '8:30 AM',  flag: '🇺🇸', name: 'CPI',         prev: '2.4%',  dir: '+', cd: '2d 14h', next: true  },
                    { time: '8:30 AM',  flag: '🇺🇸', name: 'Core CPI',    prev: '2.1%',  dir: '+', cd: '2d 14h', next: false },
                  ],
                },
                {
                  label: 'FRI, 6/13',
                  rows: [
                    { time: '8:30 AM',  flag: '🇺🇸', name: 'NFP',         prev: '177K',  dir: '+', cd: '4d 8h',  next: false },
                    { time: '8:30 AM',  flag: '🇺🇸', name: 'Unemployment', prev: '3.9%',  dir: '−', cd: '4d 8h',  next: false },
                  ],
                },
                {
                  label: 'TUE, 6/17',
                  rows: [
                    { time: '8:15 AM',  flag: '🇪🇺', name: 'ECB Rate',    prev: '4.0%',  dir: '·', cd: '8d 2h',  next: false },
                  ],
                },
                {
                  label: 'WED, 6/18',
                  rows: [
                    { time: '2:00 PM',  flag: '🇺🇸', name: 'FOMC',        prev: '4.5%',  dir: '·', cd: '10d 4h', next: false },
                  ],
                },
              ].map(group => (
                <div key={group.label} style={{ marginBottom: 6 }}>
                  <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: '1px', color: '#d8d8d8', textTransform: 'uppercase', padding: '4px 0 2px', borderBottom: '1px solid rgba(255,255,255,0.12)', marginBottom: 2 }}>
                    {group.label}
                  </div>
                  {group.rows.map((row, i) => (
                    <div key={i} style={{
                      display: 'grid', gridTemplateColumns: '50px 18px 1fr 36px 12px 32px 8px',
                      alignItems: 'center', gap: 4, padding: '4px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.03)',
                      ...(row.next ? { background: 'rgba(255,100,0,0.06)', borderRadius: 2, paddingLeft: 2, marginLeft: -2 } : {}),
                    }}>
                      <span style={{ fontSize: 9, color: '#d8d8d8', whiteSpace: 'nowrap' }}>{row.time}</span>
                      <span style={{ fontSize: 11, lineHeight: 1 }}>{row.flag}</span>
                      <span style={{ fontSize: 10, color: '#f0f0f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name}</span>
                      <span style={{ fontSize: 9, color: '#d8d8d8', textAlign: 'right' }}>{row.prev}</span>
                      <span style={{ fontSize: 10, fontWeight: 700, textAlign: 'center', color: row.dir === '+' ? '#00ff88' : row.dir === '−' ? '#ff4444' : '#444' }}>{row.dir}</span>
                      <span style={{ fontSize: 9, color: '#f39c12', textAlign: 'right', whiteSpace: 'nowrap' }}>{row.cd}</span>
                      <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#cc2200', justifySelf: 'center' }} />
                    </div>
                  ))}
                </div>
              ))}

              <div style={{ marginTop: 8, fontFamily: "'Share Tech Mono',monospace", fontSize: 7, letterSpacing: '1px', color: '#545a72', textAlign: 'center', paddingTop: 6, borderTop: '1px solid #1a1a1a' }}>⚠ ILLUSTRATIVE ONLY — NOT FINANCIAL ADVICE. FOR ANALYTICAL REFERENCE.</div>
            </div>
          </div>

          {/* Spike behavior illustration */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ background: 'var(--ic-panel2)', border: '1px solid var(--ic-border)', padding: '14px 12px 12px', height: '100%', boxSizing: 'border-box' }}>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '12px' }}>SPIKE EVENT — PRICE BEHAVIOR</div>
              <svg viewBox="0 0 320 180" width="100%" style={{ display: 'block' }}>
                <line x1="40" y1="20"  x2="310" y2="20"  stroke="#1a1d2e" strokeWidth="0.5"/>
                <line x1="40" y1="60"  x2="310" y2="60"  stroke="#1a1d2e" strokeWidth="0.5"/>
                <line x1="40" y1="100" x2="310" y2="100" stroke="#1a1d2e" strokeWidth="0.5"/>
                <line x1="40" y1="140" x2="310" y2="140" stroke="#1a1d2e" strokeWidth="0.5"/>
                <text x="2"  y="23"  fontFamily="Share Tech Mono" fontSize="6.5" fill="#545a72">HIGH</text>
                <text x="2"  y="103" fontFamily="Share Tech Mono" fontSize="6.5" fill="#545a72">MID</text>
                <text x="2"  y="143" fontFamily="Share Tech Mono" fontSize="6.5" fill="#545a72">LOW</text>
                <line x1="40" y1="155" x2="310" y2="155" stroke="#1a1d2e" strokeWidth="1"/>
                <line x1="55"  y1="88"  x2="55"  y2="112" stroke="#545a72" strokeWidth="0.5"/>
                <rect x="51"  y="92"  width="8" height="14" fill="rgba(0,229,122,0.6)"/>
                <line x1="75"  y1="82"  x2="75"  y2="108" stroke="#545a72" strokeWidth="0.5"/>
                <rect x="71"  y="86"  width="8" height="12" fill="rgba(232,0,58,0.6)"/>
                <line x1="95"  y1="85"  x2="95"  y2="110" stroke="#545a72" strokeWidth="0.5"/>
                <rect x="91"  y="89"  width="8" height="14" fill="rgba(0,229,122,0.6)"/>
                <line x1="115" y1="84"  x2="115" y2="108" stroke="#545a72" strokeWidth="0.5"/>
                <rect x="111" y="88"  width="8" height="12" fill="rgba(0,229,122,0.6)"/>
                <line x1="135" y1="80"  x2="135" y2="112" stroke="rgba(245,194,0,0.5)" strokeWidth="0.5"/>
                <rect x="131" y="88"  width="8" height="16" fill="rgba(245,194,0,0.5)"/>
                <line x1="160" y1="15" x2="160" y2="155" stroke="rgba(232,0,58,0.6)" strokeWidth="1.5" strokeDasharray="4,3"/>
                <rect x="148" y="8" width="62" height="14" fill="rgba(232,0,58,0.15)" rx="1"/>
                <text x="151" y="18" fontFamily="Share Tech Mono" fontSize="7" fill="rgba(232,0,58,0.95)" letterSpacing="0.5">HIGH IMPACT</text>
                <line x1="160" y1="22" x2="160" y2="130" stroke="rgba(232,0,58,0.7)" strokeWidth="1"/>
                <rect x="156" y="60"  width="8" height="40" fill="rgba(232,0,58,0.8)"/>
                <text x="170" y="38" fontFamily="Share Tech Mono" fontSize="6.5" fill="rgba(232,0,58,0.9)" letterSpacing="0.5">SPIKE</text>
                <text x="170" y="48" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(232,0,58,0.6)" letterSpacing="0.5">±5–8%</text>
                <line x1="185" y1="40"  x2="185" y2="120" stroke="#545a72" strokeWidth="0.5"/>
                <rect x="181" y="52"  width="8" height="38" fill="rgba(245,194,0,0.5)"/>
                <line x1="205" y1="55"  x2="205" y2="90"  stroke="#545a72" strokeWidth="0.5"/>
                <rect x="201" y="60"  width="8" height="20" fill="rgba(0,229,122,0.6)"/>
                <line x1="225" y1="52"  x2="225" y2="85"  stroke="#545a72" strokeWidth="0.5"/>
                <rect x="221" y="56"  width="8" height="18" fill="rgba(0,229,122,0.6)"/>
                <line x1="245" y1="50"  x2="245" y2="80"  stroke="#545a72" strokeWidth="0.5"/>
                <rect x="241" y="54"  width="8" height="16" fill="rgba(0,229,122,0.6)"/>
                <line x1="265" y1="46"  x2="265" y2="76"  stroke="#545a72" strokeWidth="0.5"/>
                <rect x="261" y="50"  width="8" height="16" fill="rgba(0,229,122,0.6)"/>
                <line x1="285" y1="44"  x2="285" y2="72"  stroke="#545a72" strokeWidth="0.5"/>
                <rect x="281" y="48"  width="8" height="16" fill="rgba(0,229,122,0.6)"/>
                <text x="44"  y="150" fontFamily="Share Tech Mono" fontSize="6" fill="#545a72">NORMAL CYCLE</text>
                <text x="198" y="150" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(0,229,122,0.6)">NEW STATE FORMING</text>
              </svg>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '6px', marginTop: '10px' }}>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(245,194,0,0.8)', textAlign: 'center', padding: '5px', border: '1px solid rgba(245,194,0,0.2)', background: 'rgba(245,194,0,0.04)' }}>PRE-EVENT<br/><span style={{ color: 'var(--ic-dim)' }}>HOLD flags risk</span></div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(232,0,58,0.8)', textAlign: 'center', padding: '5px', border: '1px solid rgba(232,0,58,0.2)', background: 'rgba(232,0,58,0.04)' }}>SPIKE BAR<br/><span style={{ color: 'var(--ic-dim)' }}>±5–8% in seconds</span></div>
                <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'rgba(0,229,122,0.8)', textAlign: 'center', padding: '5px', border: '1px solid rgba(0,229,122,0.2)', background: 'rgba(0,229,122,0.04)' }}>POST-EVENT<br/><span style={{ color: 'var(--ic-dim)' }}>new state settles</span></div>
              </div>
              <div style={{ marginTop: '8px', fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'var(--ic-dim)', textAlign: 'center', paddingTop: '6px', borderTop: '1px solid var(--ic-border)' }}>⚠ ILLUSTRATIVE ONLY — NOT FINANCIAL ADVICE. FOR ANALYTICAL REFERENCE.</div>
            </div>
          </div>

          </div>{/* end flex row */}
        </div>
      </section>

      <hr className="sep" />

      {/* VOLUME PROFILE + TPO */}
      <section>
        <div className="section-inner">
          <div className="section-tag">Market Structure</div>
          <h2>Volume Profile<br/>&amp; Time Price Opportunity</h2>
          <p className="section-desc">
            Price lingers where participants agree on value and races where they don't. Volume Profile and TPO map that behavior into visible acceptance and rejection zones.
          </p>

          {/* ── Top row: charts ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px', marginBottom: '32px', alignItems: 'start' }}>

            {/* Left: 30-day market profile */}
            <div>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '10px' }}>30-DAY MARKET PROFILE — ILLUSTRATIVE</div>
              <div style={{ background: 'var(--ic-panel2)', border: '1px solid var(--ic-border)', padding: '12px 10px' }}>
                <svg viewBox="0 0 310 240" width="100%" style={{ display: 'block' }}>

                  {/* Value Area fill — white @ 4% like 3D viewer VA fill */}
                  <rect x="46" y="62" width="160" height="117" fill="rgba(255,255,255,0.04)" />

                  {/* IB bracket on left — yellow #f7c948 */}
                  <line x1="10" y1="88"  x2="10" y2="166" stroke="rgba(247,201,72,0.80)" strokeWidth="1.5"/>
                  <line x1="10" y1="88"  x2="17" y2="88"  stroke="rgba(247,201,72,0.80)" strokeWidth="1.5"/>
                  <line x1="10" y1="166" x2="17" y2="166" stroke="rgba(247,201,72,0.80)" strokeWidth="1.5"/>
                  <text x="2" y="131" fontFamily="Share Tech Mono" fontSize="6" fill="rgba(247,201,72,0.70)" transform="rotate(-90,6,131)" textAnchor="middle">IB</text>

                  {/* Bars — 16 rows, each 12px tall, 1px gap */}
                  {/* Row  0 y=10  w=8  — single print */}
                  <rect x="46" y="10"  width="8"   height="12" fill="#1c1c2a"/>
                  {/* Row  1 y=23  w=14 */}
                  <rect x="46" y="23"  width="14"  height="12" fill="#1c1c2a"/>
                  {/* Row  2 y=36  w=24 */}
                  <rect x="46" y="36"  width="24"  height="12" fill="#222233"/>
                  {/* Row  3 y=49  w=42 */}
                  <rect x="46" y="49"  width="42"  height="12" fill="#2a2a3a"/>
                  {/* Row  4 y=62  w=60 — VAH / VA start */}
                  <rect x="46" y="62"  width="60"  height="12" fill="#444455"/>
                  {/* Row  5 y=75  w=80 */}
                  <rect x="46" y="75"  width="80"  height="12" fill="#444455"/>
                  {/* Row  6 y=88  w=98 — IB top */}
                  <rect x="46" y="88"  width="98"  height="12" fill="#444455"/>
                  {/* Row  7 y=101 w=118 */}
                  <rect x="46" y="101" width="118" height="12" fill="#444455"/>
                  {/* Row  8 y=114 w=148 — POC #cc2222 */}
                  <rect x="46" y="114" width="148" height="12" fill="rgba(204,34,34,0.90)"/>
                  {/* Row  9 y=127 w=124 */}
                  <rect x="46" y="127" width="124" height="12" fill="#444455"/>
                  {/* Row 10 y=140 w=102 */}
                  <rect x="46" y="140" width="102" height="12" fill="#444455"/>
                  {/* Row 11 y=153 w=78 — IB bottom */}
                  <rect x="46" y="153" width="78"  height="12" fill="#444455"/>
                  {/* Row 12 y=166 w=58 — VAL / VA end */}
                  <rect x="46" y="166" width="58"  height="12" fill="#444455"/>
                  {/* Row 13 y=179 w=36 */}
                  <rect x="46" y="179" width="36"  height="12" fill="#2a2a3a"/>
                  {/* Row 14 y=192 w=18 */}
                  <rect x="46" y="192" width="18"  height="12" fill="#222233"/>
                  {/* Row 15 y=205 w=8  — single print */}
                  <rect x="46" y="205" width="8"   height="12" fill="#1c1c2a"/>

                  {/* Level lines + labels */}
                  {/* VAH — gray slab color #555566 */}
                  <line x1="46" y1="62"  x2="210" y2="62"  stroke="rgba(85,85,102,0.90)" strokeWidth="0.75" strokeDasharray="3,5"/>
                  <text x="213" y="66" fontFamily="Share Tech Mono" fontSize="7.5" fill="rgba(170,170,190,0.75)" letterSpacing="0.5">VAH</text>

                  {/* POC — red #ff4444 label, #cc2222 line */}
                  <line x1="46" y1="120" x2="210" y2="120" stroke="rgba(204,34,34,0.65)" strokeWidth="0.75" strokeDasharray="3,5"/>
                  <text x="213" y="124" fontFamily="Share Tech Mono" fontSize="7.5" fill="#ff4444" letterSpacing="0.5">POC</text>

                  {/* MID — gray #666666 */}
                  <line x1="46" y1="108" x2="210" y2="108" stroke="rgba(102,102,102,0.55)" strokeWidth="0.75" strokeDasharray="2,6"/>
                  <text x="213" y="112" fontFamily="Share Tech Mono" fontSize="7.5" fill="#666666" letterSpacing="0.5">MID</text>

                  {/* VAL — gray slab color #555566 */}
                  <line x1="46" y1="178" x2="210" y2="178" stroke="rgba(85,85,102,0.90)" strokeWidth="0.75" strokeDasharray="3,5"/>
                  <text x="213" y="182" fontFamily="Share Tech Mono" fontSize="7.5" fill="rgba(170,170,190,0.75)" letterSpacing="0.5">VAL</text>

                  {/* IB labels — yellow #f7c948 */}
                  <text x="213" y="93"  fontFamily="Share Tech Mono" fontSize="7" fill="rgba(247,201,72,0.70)" letterSpacing="0.5">IB H</text>
                  <text x="213" y="160" fontFamily="Share Tech Mono" fontSize="7" fill="rgba(247,201,72,0.70)" letterSpacing="0.5">IB L</text>

                  {/* Single print labels */}
                  <text x="26" y="18"  fontFamily="Share Tech Mono" fontSize="5.5" fill="#444455" textAnchor="end">SINGLE</text>
                  <text x="26" y="24"  fontFamily="Share Tech Mono" fontSize="5.5" fill="#444455" textAnchor="end">PRINT</text>
                  <text x="26" y="210" fontFamily="Share Tech Mono" fontSize="5.5" fill="#444455" textAnchor="end">SINGLE</text>
                  <text x="26" y="216" fontFamily="Share Tech Mono" fontSize="5.5" fill="#444455" textAnchor="end">PRINT</text>

                  {/* Axes labels */}
                  <text x="46" y="228" fontFamily="Share Tech Mono" fontSize="5.5" fill="#545a72">LOW PRICE</text>
                  <text x="26" y="66"  fontFamily="Share Tech Mono" fontSize="5.5" fill="#545a72" textAnchor="end">HIGH</text>
                  <text x="26" y="72"  fontFamily="Share Tech Mono" fontSize="5.5" fill="#545a72" textAnchor="end">PRICE</text>

                  {/* Volume axis arrow */}
                  <line x1="46" y1="222" x2="190" y2="222" stroke="#222233" strokeWidth="1"/>
                  <polygon points="190,219 190,225 196,222" fill="#222233"/>
                  <text x="108" y="232" fontFamily="Share Tech Mono" fontSize="5.5" fill="#545a72" textAnchor="middle">VOLUME / TIME →</text>

                </svg>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '5px', marginTop: '10px' }}>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '6.5px', letterSpacing: '0.8px', color: 'rgba(170,170,190,0.7)', textAlign: 'center', padding: '5px 3px', borderTop: '1px solid rgba(68,68,85,0.8)' }}>LONG BARS<br/><span style={{ color: 'var(--ic-dim)' }}>Acceptance / balance</span></div>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '6.5px', letterSpacing: '0.8px', color: '#ff4444', textAlign: 'center', padding: '5px 3px', borderTop: '1px solid rgba(204,34,34,0.5)' }}>POC BAR<br/><span style={{ color: 'var(--ic-dim)' }}>Max time/volume</span></div>
                  <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '6.5px', letterSpacing: '0.8px', color: 'rgba(68,68,85,0.9)', textAlign: 'center', padding: '5px 3px', borderTop: '1px solid rgba(68,68,85,0.4)' }}>SHORT BARS<br/><span style={{ color: 'var(--ic-dim)' }}>Rejection / fast move</span></div>
                </div>
              </div>

            </div>

            {/* Right: TPO double distribution */}
            <div>
              <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '10px' }}>TPO — DOUBLE DISTRIBUTION</div>
              <div style={{ background: 'var(--ic-panel2)', border: '1px solid var(--ic-border)', padding: '12px 10px' }}>
                <div className="tpo-grid">
                  {[
                    { letters: ['I'],                                                              poc: false },
                    { letters: ['I','I'],                                                         poc: false },
                    { letters: ['H','H','H'],                                                     poc: false, marker: `VAH  ${btcLevels ? '$' + btcLevels.high.toLocaleString() : ''}`, mc: 'rgba(85,85,102,0.75)',  bt: '1px dashed rgba(85,85,102,0.35)' },
                    { letters: ['G','G','G','G','G','H','H','H','H'],                             poc: false, marker: '● CLOSE', mc: 'rgba(0,229,122,0.85)' },
                    { letters: ['G','G','G','G','G','G','G','H','H','H','H','H','H'],             poc: true  },
                    { letters: ['G','G','G','G','G','H','H'],                                     poc: false },
                    { letters: ['H','H'],                                                         poc: false },
                    { letters: ['G','G'],                                                         poc: false },
                    { letters: ['F'],                                                              poc: false },
                    { letters: ['E'],                                                              poc: false },
                    { letters: ['D'],                                                              poc: false },
                    { letters: ['C'],                                                              poc: false },
                    { letters: ['B','B'],                                                         poc: false },
                    { letters: ['C','C','C'],                                                     poc: false, marker: '● OPEN', mc: 'rgba(247,201,72,0.85)' },
                    { letters: ['B','B','B','B','B','B','C','C','C','C'],                         poc: false },
                    { letters: ['A','A','A','A','A','A','B','B','B','B','B','B','C','C','C'],     poc: true,  marker: `VPO  ${btcLevels ? '$' + btcLevels.vpo.toLocaleString() : 'POC'}`, mc: 'rgba(204,34,34,0.7)' },
                    { letters: ['A','A','A','A','A','A','B','B','B','B'],                         poc: false },
                    { letters: ['A','A','A','A'],                                                 poc: false, marker: `VAL  ${btcLevels ? '$' + btcLevels.low.toLocaleString() : ''}`, mc: 'rgba(85,85,102,0.75)', bb: '1px dashed rgba(85,85,102,0.35)' },
                    { letters: ['A','A'],                                                         poc: false },
                    { letters: ['A'],                                                              poc: false },
                  ].map(({ letters, poc, sep, marker, mc, bt, bb }, ri) => (
                    sep
                      ? <div key={ri} style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '5.5px', color: 'rgba(120,120,140,0.45)', padding: '3px 0 1px', letterSpacing: '0.8px' }}>{sep}</div>
                      : <div key={ri} className={`tpo-row${poc ? ' tpo-poc-row' : ''}`} style={{ borderTop: bt || undefined, borderBottom: bb || undefined, display: 'flex', alignItems: 'center' }}>
                          {letters.map((letter, li) => (
                            <span key={li} className="tpo-letter">{letter}</span>
                          ))}
                          {marker && <span style={{ marginLeft: 'auto', paddingLeft: '6px', fontFamily: "'Share Tech Mono',monospace", fontSize: '5.5px', color: mc, whiteSpace: 'nowrap', letterSpacing: '0.5px' }}>{marker}</span>}
                        </div>
                  ))}
                </div>
                <div style={{ marginTop: '8px', fontFamily: "'Share Tech Mono',monospace", fontSize: '6.5px', letterSpacing: '0.8px', color: 'var(--ic-dim)' }}>
                  Two balanced clusters separated by single prints. Red row = VPO (6M volume point of control). Dashed lines = 6M high / low. Prices update every 12h.
                </div>
              </div>
            </div>
          </div>

          {/* ── Bottom row: text blocks ── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '48px', marginBottom: '40px' }}>

            {/* Left: Volume Profile + TPO */}
            <div className="vp-text-col">
              <div className="vp-block">
                <div className="vp-block-tag">Volume Profile</div>
                <p>
                  Horizontal histogram — longer bar at a price means more volume traded there. Wide nodes mark where institutional order flow concentrated. All key levels (POC, VAH, VAL, IB, MID) derive from this distribution.
                </p>
              </div>
              <div className="vp-block">
                <div className="vp-block-tag">Time Price Opportunity (TPO)</div>
                <p>
                  Uses time instead of volume. Each bracket prints a letter at every price visited — more letters means more time spent, which signals acceptance. Many letters = fair value; one letter = pass-through.
                </p>
              </div>
            </div>

            {/* Right: Wide vs Narrow + Blockhive */}
            <div className="vp-text-col">
              <div className="vp-block">
                <div className="vp-block-tag">Wide vs. Narrow Areas</div>
                <p>
                  <strong style={{ color: 'var(--ic-bright)' }}>Wide nodes</strong> — agreed value; act as support and resistance on return visits.<br/><br/>
                  <strong style={{ color: 'var(--ic-bright)' }}>Single prints</strong> — urgent move, no negotiation. Fill later or act as a one-way launch pad.
                </p>
              </div>
              <div className="vp-block">
                <div className="vp-block-tag">Reading the Blockhive 3D Viewer</div>
                <p>
                  The right face shows the 30-day Volume Profile. The left-edge strip marks VAH, MID, IB, and VAL so you always know where price sits in the session structure.
                </p>
              </div>
            </div>

          </div>

          {/* ── Key level reference cards ── */}
          <div className="vp-levels-grid">
            <div className="vp-level-card vp-poc">
              <div className="vp-level-name">POC</div>
              <div className="vp-level-full">Point of Control</div>
              <div className="vp-level-desc">Price with the most time or volume. Acts as a magnet — price revisits here when momentum fades.</div>
            </div>
            <div className="vp-level-card vp-va">
              <div className="vp-level-name">VAH</div>
              <div className="vp-level-full">Value Area High</div>
              <div className="vp-level-desc">Upper bound of the 70% value area. Resistance from below — acceptance above signals upside range extension.</div>
            </div>
            <div className="vp-level-card vp-va">
              <div className="vp-level-name">VAL</div>
              <div className="vp-level-full">Value Area Low</div>
              <div className="vp-level-desc">Lower bound of the 70% value area. Support from above — acceptance below signals downside extension; rejection inside = reversion to POC.</div>
            </div>
            <div className="vp-level-card vp-ib">
              <div className="vp-level-name">IB</div>
              <div className="vp-level-full">Initial Balance</div>
              <div className="vp-level-desc">High-low range of the first 8 UTC hours. Extension beyond IB signals directional conviction for the session.</div>
            </div>
            <div className="vp-level-card vp-mid">
              <div className="vp-level-name">MID</div>
              <div className="vp-level-full">Session Midpoint</div>
              <div className="vp-level-desc">Midpoint of the session range. Above MID = buyers in control; below = sellers in control.</div>
            </div>
          </div>

          <div style={{ marginTop: '28px', fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'var(--ic-dim)', textAlign: 'center', paddingTop: '12px', borderTop: '1px solid var(--ic-border)' }}>
            ⚠ ILLUSTRATIVE ONLY — NOT FINANCIAL ADVICE. FOR ANALYTICAL REFERENCE.
          </div>
        </div>
      </section>

      <hr className="sep" />

      {/* AMT DAY TYPES + OPEN TYPES */}
      <section style={{ background: 'var(--ic-panel)', display: 'none' }}>
        <div className="section-inner">
          <div className="section-tag">Auction Market Theory</div>
          <h2>Day Types &amp;<br/>Open Behavior</h2>
          <p className="section-desc">
            Every session develops a recognizable profile. Identifying the day type early — using the Initial Balance, distribution structure, and open behavior — determines which side of the market to trade and where the highest-probability entries form. The six day types and four open types are the foundational framework of Auction Market Theory.
          </p>

          {/* 6 Day Types */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid var(--ic-sep)' }}>
              6 TPO DAY TYPES
            </div>
            <div className="vp-levels-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>

              {/* Normal */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-dim)' }}>Normal</div>
                <div className="vp-level-full">Balanced Session</div>
                <svg viewBox="0 0 100 70" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <line x1="4" y1="16" x2="4" y2="51" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="16" x2="9"  y2="16" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="51" x2="9"  y2="51" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <text x="2" y="36" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(247,201,72,0.6)" textAnchor="middle" transform="rotate(-90,2,34)">IB</text>
                  <rect x="10" y="2"  width="8"  height="6" fill="#1c1c2a"/>
                  <rect x="10" y="9"  width="18" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="16" width="30" height="6" fill="#444455"/>
                  <rect x="10" y="23" width="44" height="6" fill="#444455"/>
                  <rect x="10" y="30" width="52" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="37" width="44" height="6" fill="#444455"/>
                  <rect x="10" y="44" width="30" height="6" fill="#444455"/>
                  <rect x="10" y="51" width="18" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="58" width="8"  height="6" fill="#1c1c2a"/>
                  <line x1="63" y1="33" x2="66" y2="33" stroke="rgba(204,34,34,0.5)" strokeWidth="0.7"/>
                  <text x="68" y="36" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(204,34,34,0.65)">POC</text>
                </svg>
                <div className="vp-level-desc">IB covers 40–60 % of the session range. Both extremes briefly explored but held. Closes within or near the IB. Responsive activity dominates — fade extremes, target the POC as the session magnet.</div>
              </div>

              {/* Normal Variation */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-cyan)' }}>Norm Var</div>
                <div className="vp-level-full">Normal Variation</div>
                <svg viewBox="0 0 100 70" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <line x1="4" y1="37" x2="4" y2="58" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="37" x2="9"  y2="37" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="58" x2="9"  y2="58" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <text x="2" y="50" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(247,201,72,0.6)" textAnchor="middle" transform="rotate(-90,2,48)">IB</text>
                  <rect x="10" y="2"  width="14" height="6" fill="#1d2828"/>
                  <rect x="10" y="9"  width="24" height="6" fill="#223535"/>
                  <rect x="10" y="16" width="36" height="6" fill="#2a4242"/>
                  <rect x="10" y="23" width="48" height="6" fill="#334e4e"/>
                  <rect x="10" y="30" width="54" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="37" width="42" height="6" fill="#444455"/>
                  <rect x="10" y="44" width="30" height="6" fill="#333344"/>
                  <rect x="10" y="51" width="18" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="58" width="8"  height="6" fill="#1c1c2a"/>
                  <line x1="65" y1="33" x2="68" y2="33" stroke="rgba(204,34,34,0.5)" strokeWidth="0.7"/>
                  <text x="70" y="36" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(204,34,34,0.65)">POC</text>
                  <text x="67" y="14" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(0,229,122,0.6)">▲EXT</text>
                </svg>
                <div className="vp-level-desc">Starts balanced, then one side of the IB breaks with directional conviction. The other IB extreme holds. Directional close. Trade in the direction of the IB break — the old IB extreme flips to support or resistance.</div>
              </div>

              {/* Trend */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-green)' }}>Trend</div>
                <div className="vp-level-full">Trend Day</div>
                <svg viewBox="0 0 100 70" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <line x1="4" y1="51" x2="4" y2="65" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="51" x2="9"  y2="51" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="65" x2="9"  y2="65" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <text x="2" y="60" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(247,201,72,0.6)" textAnchor="middle" transform="rotate(-90,2,59)">IB</text>
                  <rect x="10" y="2"  width="32" height="6" fill="#3a5252"/>
                  <rect x="10" y="9"  width="29" height="6" fill="#2d4242"/>
                  <rect x="10" y="16" width="26" height="6" fill="#233636"/>
                  <rect x="10" y="23" width="24" height="6" fill="#1d2d2d"/>
                  <rect x="10" y="30" width="24" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="37" width="22" height="6" fill="#1c2525"/>
                  <rect x="10" y="44" width="20" height="6" fill="#1a2020"/>
                  <rect x="10" y="51" width="12" height="6" fill="#1c1c2a"/>
                  <rect x="10" y="58" width="10" height="6" fill="#1c1c2a"/>
                  <text x="46" y="7"  fontFamily="Share Tech Mono" fontSize="5" fill="rgba(0,229,122,0.7)">▲ CLOSE</text>
                  <line x1="35" y1="33" x2="55" y2="33" stroke="rgba(204,34,34,0.4)" strokeWidth="0.6" strokeDasharray="2,2"/>
                  <text x="57" y="36" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(204,34,34,0.65)">POC</text>
                </svg>
                <div className="vp-level-desc">Very narrow IB — market opens and drives immediately. Single distribution extends all session. Close near the extreme. Never fade. Add on shallow pullbacks; early entry is critical. One of the highest-conviction day types.</div>
              </div>

              {/* Double Distribution */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-cyan)' }}>2-Dist</div>
                <div className="vp-level-full">Double-Distribution</div>
                <svg viewBox="0 0 100 76" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <line x1="4" y1="44" x2="4" y2="62" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="44" x2="9"  y2="44" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="62" x2="9"  y2="62" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <text x="2" y="55" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(247,201,72,0.6)" textAnchor="middle" transform="rotate(-90,2,53)">IB</text>
                  <rect x="10" y="2"  width="12" height="6" fill="#1d2828"/>
                  <rect x="10" y="9"  width="30" height="6" fill="#334a4a"/>
                  <rect x="10" y="16" width="44" height="6" fill="#3a5252"/>
                  <rect x="10" y="23" width="38" height="6" fill="#2d4040"/>
                  <rect x="10" y="30" width="18" height="6" fill="#1d2828"/>
                  <rect x="10" y="37" width="5"  height="6" fill="#181818"/>
                  <rect x="10" y="44" width="20" height="6" fill="#333344"/>
                  <rect x="10" y="51" width="40" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="58" width="26" height="6" fill="#333344"/>
                  <rect x="10" y="65" width="10" height="6" fill="#1c1c2a"/>
                  <text x="58" y="18" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(0,229,204,0.6)">2ND</text>
                  <text x="18" y="43" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(255,255,255,0.2)">SP</text>
                  <text x="58" y="56" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(204,34,34,0.6)">1ST</text>
                </svg>
                <div className="vp-level-desc">Narrow IB forms the first distribution. A catalyst triggers migration through single prints to a second distribution. Single prints between the two distributions become critical support or resistance. Best setup: enter the second distribution on a pullback to the single-print zone.</div>
              </div>

              {/* Nontrend */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-dim)' }}>Nontrend</div>
                <div className="vp-level-full">Inside / Compression</div>
                <svg viewBox="0 0 100 70" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <rect x="10" y="4"  width="52" height="62" fill="rgba(255,255,255,0.015)" stroke="rgba(68,68,85,0.3)" strokeWidth="0.8" strokeDasharray="3,3"/>
                  <text x="12" y="10" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(68,68,85,0.55)">PRIOR RANGE</text>
                  <rect x="10" y="28" width="14" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="35" width="22" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="42" width="14" height="6" fill="#2a2a3a"/>
                  <text x="66" y="38" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(68,68,85,0.8)">← TIGHT</text>
                </svg>
                <div className="vp-level-desc">Price stays inside the prior session's IB. No conviction in either direction. Low volume, tight range. Avoid initiating new positions — compression signals accumulation before a directional expansion. Wait for the next session's break.</div>
              </div>

              {/* Neutral */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-yellow)' }}>Neutral</div>
                <div className="vp-level-full">Neutral Day</div>
                <svg viewBox="0 0 100 70" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <line x1="4" y1="23" x2="4" y2="44" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="23" x2="9"  y2="23" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <line x1="4" y1="44" x2="9"  y2="44" stroke="rgba(247,201,72,0.7)" strokeWidth="1.2"/>
                  <text x="2" y="36" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(247,201,72,0.6)" textAnchor="middle" transform="rotate(-90,2,34)">IB</text>
                  <rect x="10" y="2"  width="8"  height="6" fill="#1c1c2a"/>
                  <rect x="10" y="9"  width="18" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="16" width="28" height="6" fill="#333344"/>
                  <rect x="10" y="23" width="36" height="6" fill="#444455"/>
                  <rect x="10" y="30" width="52" height="6" fill="rgba(204,34,34,0.85)"/>
                  <rect x="10" y="37" width="36" height="6" fill="#444455"/>
                  <rect x="10" y="44" width="28" height="6" fill="#333344"/>
                  <rect x="10" y="51" width="18" height="6" fill="#2a2a3a"/>
                  <rect x="10" y="58" width="8"  height="6" fill="#1c1c2a"/>
                  <line x1="63" y1="33" x2="66" y2="33" stroke="rgba(204,34,34,0.5)" strokeWidth="0.7"/>
                  <text x="68" y="36" fontFamily="Share Tech Mono" fontSize="5" fill="rgba(204,34,34,0.65)">POC</text>
                  <text x="40" y="9"  fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(255,68,68,0.5)">↑BROKE</text>
                  <text x="40" y="64" fontFamily="Share Tech Mono" fontSize="4.5" fill="rgba(255,68,68,0.5)">↓BROKE</text>
                </svg>
                <div className="vp-level-desc">Breaks both sides of the IB but closes in the middle of the session range. Buyers and sellers equally active — neither wins the day. Fade both extremes; the POC is the gravitational center. Expect mean reversion into the close.</div>
              </div>

            </div>
          </div>

          {/* 4 Open Types */}
          <div style={{ marginBottom: '32px' }}>
            <div style={{ fontFamily: "'Share Tech Mono',monospace", fontSize: '9px', letterSpacing: '3px', color: 'var(--ic-dim)', marginBottom: '14px', paddingBottom: '8px', borderBottom: '1px solid var(--ic-sep)' }}>
              4 OPEN TYPES
            </div>
            <div className="vp-levels-grid" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))' }}>

              {/* OD — Open-Drive */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-green)', fontSize: '11px' }}>OD</div>
                <div className="vp-level-full">Open-Drive</div>
                <svg viewBox="0 0 100 65" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <rect x="5" y="22" width="30" height="32" fill="rgba(68,68,85,0.12)" stroke="rgba(85,85,102,0.4)" strokeWidth="0.8"/>
                  <text x="7"  y="31" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAH</text>
                  <line x1="5" y1="38" x2="35" y2="38" stroke="rgba(204,34,34,0.35)" strokeWidth="0.7" strokeDasharray="2,2"/>
                  <text x="7"  y="44" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(204,34,34,0.45)">POC</text>
                  <text x="7"  y="53" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAL</text>
                  <circle cx="36" cy="22" r="2.2" fill="rgba(0,229,204,0.85)"/>
                  <polyline points="36,22 52,14 68,8 82,4" fill="none" stroke="rgba(0,229,122,0.85)" strokeWidth="1.5"/>
                  <polygon points="82,4 78,7 80,10" fill="rgba(0,229,122,0.85)"/>
                  <text x="38" y="62" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(0,229,122,0.45)">never returns to open</text>
                </svg>
                <div className="vp-level-desc">Market opens and drives immediately — price never returns to the opening level. Pure initiative activity from a dominant timeframe participant. Strongest open signal. Trade with the drive direction; the opening price is the stop reference.</div>
              </div>

              {/* OTD — Open-Test-Drive */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-cyan)', fontSize: '11px' }}>OTD</div>
                <div className="vp-level-full">Open-Test-Drive</div>
                <svg viewBox="0 0 100 65" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <rect x="5" y="22" width="30" height="32" fill="rgba(68,68,85,0.12)" stroke="rgba(85,85,102,0.4)" strokeWidth="0.8"/>
                  <text x="7"  y="31" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAH</text>
                  <line x1="5" y1="38" x2="35" y2="38" stroke="rgba(204,34,34,0.35)" strokeWidth="0.7" strokeDasharray="2,2"/>
                  <text x="7"  y="53" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAL</text>
                  <line x1="36" y1="22" x2="95" y2="22" stroke="rgba(85,85,102,0.35)" strokeWidth="0.8" strokeDasharray="3,2"/>
                  <text x="55" y="20" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.55)">REF</text>
                  <circle cx="36" cy="16" r="2.2" fill="rgba(0,229,204,0.85)"/>
                  <polyline points="36,16 48,22 58,22" fill="none" stroke="rgba(0,229,122,0.5)" strokeWidth="1.2"/>
                  <circle cx="58" cy="22" r="2.2" fill="rgba(247,201,72,0.85)"/>
                  <polyline points="58,22 68,14 80,7 90,3" fill="none" stroke="rgba(0,229,122,0.85)" strokeWidth="1.5"/>
                  <polygon points="90,3 86,6 88,9" fill="rgba(0,229,122,0.85)"/>
                  <text x="40" y="62" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(247,201,72,0.45)">test → hold → drive</text>
                </svg>
                <div className="vp-level-desc">Opens, tests a prior reference level (previous VAH, VAL, POC, or overnight extreme), that level holds, then drives away. The test-and-reject sequence is the entry signal — enter on the rejection of the reference with the reference as your stop.</div>
              </div>

              {/* ORR — Open-Rejection-Reverse */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-orange)', fontSize: '11px' }}>ORR</div>
                <div className="vp-level-full">Open-Rejection-Reverse</div>
                <svg viewBox="0 0 100 65" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <rect x="5" y="24" width="30" height="32" fill="rgba(68,68,85,0.12)" stroke="rgba(85,85,102,0.4)" strokeWidth="0.8"/>
                  <text x="7"  y="33" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAH</text>
                  <line x1="5" y1="40" x2="35" y2="40" stroke="rgba(204,34,34,0.35)" strokeWidth="0.7" strokeDasharray="2,2"/>
                  <text x="7"  y="55" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAL</text>
                  <circle cx="36" cy="14" r="2.2" fill="rgba(0,229,204,0.85)"/>
                  <polyline points="36,14 44,20 50,24" fill="none" stroke="rgba(255,68,68,0.5)" strokeWidth="1.2"/>
                  <circle cx="50" cy="24" r="2.2" fill="rgba(255,120,0,0.85)"/>
                  <text x="53" y="23" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(255,120,0,0.7)">REJ</text>
                  <polyline points="50,24 58,32 66,40 76,52 86,60" fill="none" stroke="rgba(255,68,68,0.85)" strokeWidth="1.5"/>
                  <polygon points="86,60 82,57 85,54" fill="rgba(255,68,68,0.85)"/>
                  <text x="5" y="63" fontFamily="Share Tech Mono" fontSize="3.8" fill="rgba(255,68,68,0.4)">outside → rejected → inside</text>
                </svg>
                <div className="vp-level-desc">Opens outside the prior Value Area, then gets rejected back inside. Responsive activity overwhelms initiative. Once price accepts back inside the VA, trade toward the POC or the opposite VA extreme — the outside probe was a failed auction.</div>
              </div>

              {/* OA — Open-Auction */}
              <div className="vp-level-card">
                <div className="vp-level-name" style={{ color: 'var(--ic-dim)', fontSize: '11px' }}>OA</div>
                <div className="vp-level-full">Open-Auction</div>
                <svg viewBox="0 0 100 65" width="100%" style={{ display: 'block', margin: '8px 0' }}>
                  <rect x="5" y="10" width="30" height="48" fill="rgba(68,68,85,0.12)" stroke="rgba(85,85,102,0.4)" strokeWidth="0.8"/>
                  <text x="7"  y="19" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAH</text>
                  <line x1="5" y1="34" x2="35" y2="34" stroke="rgba(204,34,34,0.35)" strokeWidth="0.7" strokeDasharray="2,2"/>
                  <text x="7"  y="32" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(204,34,34,0.45)">POC</text>
                  <text x="7"  y="58" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.7)">VAL</text>
                  <circle cx="36" cy="30" r="2.2" fill="rgba(0,229,204,0.85)"/>
                  <polyline points="36,30 44,24 52,34 60,26 68,34 76,28 84,32" fill="none" stroke="rgba(180,180,180,0.45)" strokeWidth="1.3"/>
                  <text x="36" y="62" fontFamily="Share Tech Mono" fontSize="4" fill="rgba(85,85,102,0.5)">balanced — await IB break</text>
                </svg>
                <div className="vp-level-desc">Opens inside the prior Value Area. Balanced, two-way trade near the open. Weakest directional signal — await IB formation then trade the IB break. An out-of-range open signals directional intent; an in-range open signals continuation of accepted value.</div>
              </div>

            </div>
          </div>

          {/* TPO Count */}
          <div className="vp-block">
            <div className="vp-block-tag">TPO Count — Reading Buyer vs. Seller Control</div>
            <p>
              After the session closes, count TPO letters above the POC against letters below the POC — exclude the tail letters at the upper and lower extremes. If letters above significantly outnumber those below, buyers controlled time and price: bullish lean for the next session. The reverse signals sellers winning the time battle. A 30 %+ imbalance is considered meaningful. Pair TPO count with day type for the highest-conviction reads — a <strong style={{ color: 'var(--ic-bright)' }}>Trend Up day with heavy TPO imbalance above POC</strong> is institutional conviction; a <strong style={{ color: 'var(--ic-bright)' }}>Neutral day with balanced TPO count</strong> confirms indecision and signals a range-trade environment.
            </p>
          </div>

          <div style={{ marginTop: '28px', fontFamily: "'Share Tech Mono',monospace", fontSize: '7px', letterSpacing: '1px', color: 'var(--ic-dim)', textAlign: 'center', paddingTop: '12px', borderTop: '1px solid var(--ic-border)' }}>
            ⚠ ILLUSTRATIVE ONLY — NOT FINANCIAL ADVICE. FOR ANALYTICAL REFERENCE.
          </div>
        </div>
      </section>

    </div>
  );
}
